import React from "react";
import {
  X,
  GitBranch,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
} from "lucide-react";
import { TenantIngressHistory } from "../../services/tenant.service";

const STATUS_CONFIG: Record<
  string,
  { icon: React.ReactNode; label: string; cls: string }
> = {
  SUCCESS: {
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    label: "Success",
    cls: "text-emerald-600 bg-emerald-50 border-emerald-200",
  },
  FAILED: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    label: "Failed",
    cls: "text-red-600 bg-red-50 border-red-200",
  },
  RUNNING: {
    icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
    label: "Running",
    cls: "text-blue-600 bg-blue-50 border-blue-200",
  },
  PENDING: {
    icon: <Clock className="w-3.5 h-3.5" />,
    label: "Pending",
    cls: "text-amber-600 bg-amber-50 border-amber-200",
  },
};

interface IngressHistoryModalProps {
  isOpen: boolean;
  tenantName: string;
  tenantKey: string;
  history: TenantIngressHistory[];
  loading?: boolean;
  onClose: () => void;
}

const IngressHistoryModal: React.FC<IngressHistoryModalProps> = ({
  isOpen,
  tenantName,
  tenantKey,
  history,
  loading = false,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: "80vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Ingress Sync History
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {tenantName}
              {tenantKey && (
                <span className="font-mono ml-1">— {tenantKey}</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <GitBranch className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-600">
                No sync history
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Ingress deployment history will appear here once synced
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => {
                const key = (h.status ?? "PENDING").toUpperCase();
                const cfg = STATUS_CONFIG[key] ?? STATUS_CONFIG["PENDING"];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg border border-slate-100"
                  >
                    <span
                      className={`flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-semibold shrink-0 ${cfg.cls}`}
                    >
                      {cfg.icon}
                      {cfg.label}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
                      <GitBranch className="w-3 h-3" />
                      <span className="font-mono">
                        {h.commit_id.slice(0, 10)}
                      </span>
                    </span>
                    <span className="text-xs text-slate-500 capitalize">
                      {h.action}
                    </span>
                    <span className="ml-auto text-xs text-slate-400 shrink-0">
                      {new Date(h.timestamp).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IngressHistoryModal;
